import React, { useState } from 'react';
import axios from 'axios';
import './ExportDialog.css';

const API = '/api/cms';

const ExportDialog = ({ apps, onClose, showMsg, token, currentFilters = {} }) => {
  const [format, setFormat] = useState('csv');
  const [loading, setLoading] = useState(false);

  const h = { Authorization: `Bearer ${token}` };

  const exportData = async () => {
    if (apps.length === 0) {
      showMsg('⚠️ Нет данных для экспорта');
      return;
    }

    setLoading(true);
    try {
      if (format === 'csv' || format === 'xlsx' || format === 'pdf') {
        await serverExport(format);
      } else if (format === 'json') {
        clientExportJSON();
      }
      showMsg(`✅ Экспорт завершён (${apps.length} записей)`);
      onClose();
    } catch (e) {
      showMsg(e.response?.data?.message || 'Ошибка экспорта');
    } finally {
      setLoading(false);
    }
  };

  // Серверный экспорт (CSV, XLSX, PDF)
  const serverExport = async (fmt) => {
    const params = new URLSearchParams();
    params.append('format', fmt);

    // Передаём текущие фильтры на сервер для экспорта
    if (currentFilters.status) params.append('status', currentFilters.status);
    if (currentFilters.priority) params.append('priority', currentFilters.priority);
    if (currentFilters.tags) params.append('tags', currentFilters.tags);
    if (currentFilters.assignedTo) params.append('assignedTo', currentFilters.assignedTo);
    if (currentFilters.category) params.append('category', currentFilters.category);
    if (currentFilters.source) params.append('source', currentFilters.source);

    try {
      const response = await axios.get(`${API}/export/applications?${params}`, {
        headers: h,
        responseType: 'blob'
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `applications.${fmt}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      const mime = response.headers['content-type'] || 'application/octet-stream';
      const url = URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      // Если серверный экспорт не работает — используем клиентский
      if (fmt === 'csv') {
        clientExportCSV();
      } else {
        throw new Error('Серверный экспорт недоступен. Убедитесь, что установлены библиотеки exceljs/pdfkit.');
      }
    }
  };

  // Клиентский экспорт CSV (запасной вариант)
  const clientExportCSV = () => {
    const headers = ['ID', 'Имя', 'Телефон', 'Email', 'Услуга', 'Статус', 'Приоритет', 'Ответственный', 'Дата создания', 'Дедлайн', 'Теги'];
    const rows = apps.map(app => [
      app._id,
      app.name,
      app.phone,
      app.email,
      app.service || '',
      app.status,
      app.priority,
      app.assignedTo?.name || 'Не назначен',
      new Date(app.createdAt).toLocaleString('ru-RU'),
      app.dueDate ? new Date(app.dueDate).toLocaleString('ru-RU') : '',
      (app.tags || []).join('; ')
    ]);

    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    downloadFile(csvContent, 'applications.csv', 'text/csv;charset=utf-8;');
  };

  // Клиентский экспорт JSON
  const clientExportJSON = () => {
    const jsonContent = JSON.stringify(apps, null, 2);
    downloadFile(jsonContent, 'applications.json', 'application/json');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="export-dialog-overlay" onClick={onClose}>
      <div className="export-dialog" onClick={e => e.stopPropagation()}>
        <div className="export-dialog-header">
          <h3>📤 Экспорт данных</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="export-dialog-body">
          <p>Будет экспортировано записей: <strong>{apps.length}</strong></p>
          
          <div className="form-group">
            <label>Формат экспорта:</label>
            <div className="format-options">
              <label className={`format-option ${format === 'csv' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={e => setFormat(e.target.value)}
                />
                <span className="format-icon">📊</span>
                <span className="format-name">CSV</span>
                <span className="format-desc">Для Excel, Google Sheets</span>
              </label>
              <label className={`format-option ${format === 'xlsx' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="xlsx"
                  checked={format === 'xlsx'}
                  onChange={e => setFormat(e.target.value)}
                />
                <span className="format-icon">📗</span>
                <span className="format-name">Excel</span>
                <span className="format-desc">Формат XLSX</span>
              </label>
              <label className={`format-option ${format === 'pdf' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={e => setFormat(e.target.value)}
                />
                <span className="format-icon">📕</span>
                <span className="format-name">PDF</span>
                <span className="format-desc">Документ для печати</span>
              </label>
              <label className={`format-option ${format === 'json' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="json"
                  checked={format === 'json'}
                  onChange={e => setFormat(e.target.value)}
                />
                <span className="format-icon">📋</span>
                <span className="format-name">JSON</span>
                <span className="format-desc">Для разработчиков</span>
              </label>
            </div>
          </div>
        </div>

        <div className="export-dialog-footer">
          <button className="btn btn-outline" onClick={onClose}>Отмена</button>
          <button
            className="btn btn-primary"
            onClick={exportData}
            disabled={loading}
          >
            {loading ? '⏳ Экспорт...' : '📥 Скачать'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;
